import React, { useState, useCallback, useMemo } from 'react';
import { Upload, Search, AlertTriangle, FileText, Download, Trash2, CheckCircle } from 'lucide-react';

const CUIDetectionTool = () => {
  const [files, setFiles] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [results, setResults] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('file');

  // Official CUI Registry Data from NARA
  const cuiCategories = useMemo(() => [
    { name: "Accident Investigation", abbreviation: "AIV", marking: "CUI//SP-AIV", group: "Law Enforcement", keywords: ["accident investigation", "crash analysis", "incident report", "safety investigation"] },
    { name: "Administrative Proceedings", abbreviation: "ADPO", marking: "CUI", group: "Legal", keywords: ["administrative proceeding", "administrative hearing", "regulatory action", "enforcement proceeding"] },
    { name: "Agriculture", abbreviation: "AG", marking: "CUI", group: "Intelligence", keywords: ["crop data", "livestock information", "agricultural statistics", "farm data"] },
    { name: "Ammonium Nitrate", abbreviation: "CRITAN", marking: "CUI//SP-CRITAN", group: "Critical Infrastructure", keywords: ["ammonium nitrate", "fertilizer security", "explosive precursor"] },
    { name: "Archaeological Resources", abbreviation: "ARCHR", marking: "CUI", group: "Natural and Cultural Resources", keywords: ["archaeological site", "cultural resource", "historic artifact", "burial site"] },
    { name: "Bank Secrecy", abbreviation: "FSEC", marking: "CUI//SP-FSEC", group: "Financial", keywords: ["bank secrecy act", "suspicious activity report", "SAR", "CTR", "currency transaction"] },
    { name: "Budget", abbreviation: "BUDG", marking: "CUI//SP-BUDG", group: "Financial", keywords: ["budget information", "fiscal data", "appropriations", "financial planning"] },
    { name: "Chemical-terrorism Vulnerability Information", abbreviation: "CVI", marking: "CUI//SP-CVI", group: "Critical Infrastructure", keywords: ["chemical vulnerability", "CVI", "chemical facility", "terrorist target"] },
    { name: "Child Pornography", abbreviation: "CHLD", marking: "CUI//SP-CHLD", group: "Legal", keywords: ["child exploitation", "child pornography", "CSAM", "child abuse material"] },
    { name: "Controlled Substances", abbreviation: "SUB", marking: "CUI//SP-SUB", group: "Law Enforcement", keywords: ["controlled substance", "drug schedule", "DEA", "narcotic", "prescription drug"] },
    { name: "Controlled Technical Information", abbreviation: "CTI", marking: "CUI//SP-CTI", group: "Defense", keywords: ["technical data", "controlled technical information", "CTI", "defense technical data"] },
    { name: "Criminal History Records Information", abbreviation: "CHRI", marking: "CUI//SP-CHRI", group: "Law Enforcement", keywords: ["criminal history", "rap sheet", "CHRI", "criminal record", "arrest record"] },
    { name: "Critical Energy Infrastructure Information", abbreviation: "CEII", marking: "CUI//SP-CEII", group: "Critical Infrastructure", keywords: ["energy infrastructure", "power grid", "electrical system", "CEII"] },
    { name: "Export Controlled", abbreviation: "EXPT", marking: "CUI//SP-EXPT", group: "Export Control", keywords: ["export control", "ITAR", "EAR", "dual use technology", "export license"] },
    { name: "Federal Grand Jury", abbreviation: "JURY", marking: "CUI//SP-JURY", group: "Legal", keywords: ["grand jury", "jury proceeding", "grand jury testimony", "jury deliberation"] },
    { name: "Federal Taxpayer Information", abbreviation: "TAX", marking: "CUI//SP-TAX", group: "Tax", keywords: ["tax return", "taxpayer information", "IRS data", "tax record", "1040"] },
    { name: "Foreign Intelligence Surveillance Act", abbreviation: "FISA", marking: "CUI//SP-FISA", group: "Intelligence", keywords: ["FISA", "foreign intelligence", "surveillance warrant", "FISC"] },
    { name: "General Intelligence", abbreviation: "INTEL", marking: "CUI//SP-INTEL", group: "Intelligence", keywords: ["intelligence report", "intel brief", "classified downgrade", "intelligence analysis"] },
    { name: "General Privacy", abbreviation: "PRVCY", marking: "CUI//SP-PRVCY", group: "Privacy", keywords: ["personally identifiable information", "PII", "privacy act", "personal data"] },
    { name: "General Proprietary Business Information", abbreviation: "PROPIN", marking: "CUI//SP-PROPIN", group: "Proprietary Business Information", keywords: ["proprietary information", "trade secret", "business confidential", "commercial information"] },
    { name: "Health Information", abbreviation: "HLTH", marking: "CUI//SP-HLTH", group: "Privacy", keywords: ["medical record", "health information", "HIPAA", "patient data", "PHI"] },
    { name: "Informant", abbreviation: "INF", marking: "CUI//SP-INF", group: "Law Enforcement", keywords: ["confidential informant", "CI", "informant identity", "source protection"] },
    { name: "Investigation", abbreviation: "INV", marking: "CUI//SP-INV", group: "Law Enforcement", keywords: ["investigation", "criminal probe", "law enforcement investigation", "investigative report"] },
    { name: "Naval Nuclear Propulsion Information", abbreviation: "NNPI", marking: "CUI//SP-NNPI", group: "Defense", keywords: ["naval nuclear", "nuclear propulsion", "submarine reactor", "NNPI"] },
    { name: "Nuclear Security-Related Information", abbreviation: "SRI", marking: "CUI//SP-SRI", group: "Nuclear", keywords: ["nuclear security", "SRI", "safeguards information", "nuclear protection"] },
    { name: "Personnel Records", abbreviation: "PERS", marking: "CUI//SP-PERS", group: "Privacy", keywords: ["personnel record", "employee file", "human resources", "staff record"] },
    { name: "Protected Critical Infrastructure Information", abbreviation: "PCII", marking: "CUI//SP-PCII", group: "Critical Infrastructure", keywords: ["PCII", "critical infrastructure information", "infrastructure vulnerability", "DHS PCII"] },
    { name: "Safeguards Information", abbreviation: "SGI", marking: "CUI//SP-SGI", group: "Nuclear", keywords: ["safeguards information", "SGI", "nuclear safeguards", "nuclear security plan"] },
    { name: "Sensitive Security Information", abbreviation: "SSI", marking: "CUI//SP-SSI", group: "Transportation", keywords: ["SSI", "sensitive security information", "transportation security", "aviation security"] },
    { name: "Source Selection", abbreviation: "SSEL", marking: "CUI//SP-SSEL", group: "Procurement and Acquisition", keywords: ["source selection", "contract award", "proposal evaluation", "procurement decision"] },
    { name: "Student Records", abbreviation: "STUD", marking: "CUI//SP-STUD", group: "Privacy", keywords: ["student record", "educational record", "FERPA", "academic record"] },
    { name: "Unclassified Controlled Nuclear Information - Energy", abbreviation: "UCNI", marking: "CUI//SP-UCNI", group: "Nuclear", keywords: ["UCNI", "nuclear information", "DOE nuclear", "nuclear data"] },
  ], []);

  const analyzeText = useCallback(async (text) => {
    const detectedCUI = [];
    
    cuiCategories.forEach(category => {
      let matches = [];
      let confidence = 0;
      
      category.keywords.forEach(keyword => {
        const regex = new RegExp(keyword.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        const keywordMatches = text.match(regex);
        
        if (keywordMatches) {
          matches.push(...keywordMatches);
          confidence += keywordMatches.length * 10;
        }
      });
      
      const nameRegex = new RegExp(category.name.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      const nameMatches = text.match(nameRegex);
      if (nameMatches) {
        matches.push(...nameMatches);
        confidence += nameMatches.length * 20;
      }
      
      const abbrevRegex = new RegExp(`\\b${category.abbreviation}\\b`, 'gi');
      const abbrevMatches = text.match(abbrevRegex);
      if (abbrevMatches) {
        matches.push(...abbrevMatches);
        confidence += abbrevMatches.length * 15;
      }
      
      const markingRegex = new RegExp(category.marking.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      const markingMatches = text.match(markingRegex);
      if (markingMatches) {
        matches.push(...markingMatches);
        confidence += markingMatches.length * 25;
      }
      
      if (matches.length > 0) {
        detectedCUI.push({
          category: category.name,
          abbreviation: category.abbreviation,
          marking: category.marking,
          group: category.group,
          matches: [...new Set(matches)],
          confidence: Math.min(confidence, 100),
          risk: confidence > 50 ? 'High' : confidence > 20 ? 'Medium' : 'Low'
        });
      }
    });
    
    return detectedCUI.sort((a, b) => b.confidence - a.confidence);
  }, [cuiCategories]);

  const handleFileUpload = async (event) => {
    const uploadedFiles = Array.from(event.target.files);
    
    for (const file of uploadedFiles) {
      try {
        const text = await readFileAsText(file);
        const newFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          content: text,
          status: 'uploaded'
        };
        
        setFiles(prev => [...prev, newFile]);
      } catch (error) {
        console.error('Error reading file:', error);
      }
    }
    
    event.target.value = '';
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const analyzeAllContent = async () => {
    setIsAnalyzing(true);
    const allResults = [];
    
    try {
      if (textInput.trim()) {
        const textResults = await analyzeText(textInput);
        if (textResults.length > 0) {
          allResults.push({
            source: 'Text Input',
            type: 'text',
            results: textResults,
            content: textInput
          });
        }
      }
      
      for (const file of files) {
        if (file.content) {
          const fileResults = await analyzeText(file.content);
          if (fileResults.length > 0) {
            allResults.push({
              source: file.name,
              type: 'file',
              results: fileResults,
              content: file.content,
              fileSize: file.size
            });
          }
        }
      }
      
      setResults(allResults);
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const clearAll = () => {
    setFiles([]);
    setTextInput('');
    setResults([]);
  };

  const exportResults = () => {
    const report = {
      timestamp: new Date().toISOString(),
      summary: {
        totalSources: results.length,
        totalCategories: results.reduce((acc, r) => acc + r.results.length, 0),
        highRiskFindings: results.reduce((acc, r) => acc + r.results.filter(res => res.risk === 'High').length, 0)
      },
      findings: results
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cui-analysis-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-red-600 bg-red-50 border-red-200';
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          CUI Detection Tool
        </h1>
        <p className="text-gray-600 mb-4">
          Analyze files and text for Controlled Unclassified Information (CUI) using the official NARA registry
        </p>
        
        {results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{results.length}</div>
              <div className="text-sm text-gray-600">Sources Analyzed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {results.reduce((acc, r) => acc + r.results.length, 0)}
              </div>
              <div className="text-sm text-gray-600">CUI Categories Found</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {results.reduce((acc, r) => acc + r.results.filter(res => res.risk === 'High').length, 0)}
              </div>
              <div className="text-sm text-gray-600">High Risk Findings</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{cuiCategories.length}</div>
              <div className="text-sm text-gray-600">Total CUI Categories</div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-8">
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setActiveTab('file')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'file'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Upload className="inline-block w-4 h-4 mr-2" />
            File Upload
          </button>
          <button
            onClick={() => setActiveTab('text')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'text'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FileText className="inline-block w-4 h-4 mr-2" />
            Text Input
          </button>
        </div>

        {activeTab === 'file' && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <label className="cursor-pointer">
              <span className="text-lg font-medium text-gray-700">
                Click to upload files or drag and drop
              </span>
              <input
                type="file"
                multiple
                accept=".txt,.doc,.docx,.pdf,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <p className="text-gray-500 text-sm mt-2">
              Supports TXT, DOC, DOCX, PDF, CSV files
            </p>
          </div>
        )}

        {activeTab === 'text' && (
          <div>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste text content here for CUI analysis..."
              className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Files</h3>
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-500 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">{file.name}</div>
                    <div className="text-sm text-gray-500">{formatFileSize(file.size)}</div>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4 mb-8">
        <button
          onClick={analyzeAllContent}
          disabled={isAnalyzing || (files.length === 0 && !textInput.trim())}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
        >
          {isAnalyzing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Analyzing...
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              Analyze for CUI
            </>
          )}
        </button>
        
        {results.length > 0 && (
          <>
            <button
              onClick={exportResults}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </button>
            <button
              onClick={clearAll}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 flex items-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </button>
          </>
        )}
      </div>

      {results.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Analysis Results</h2>
          
          {results.map((result, index) => (
            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  {result.source}
                  {result.fileSize && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({formatFileSize(result.fileSize)})
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {result.results.length} CUI categor{result.results.length === 1 ? 'y' : 'ies'} detected
                </p>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {result.results.map((finding, findingIndex) => (
                    <div
                      key={findingIndex}
                      className={`p-4 rounded-lg border ${getRiskColor(finding.risk)}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-lg">{finding.category}</h4>
                          <p className="text-sm opacity-75">
                            Group: {finding.group} | Abbreviation: {finding.abbreviation}
                          </p>
                          <p className="text-sm opacity-75">
                            CUI Marking: <code className="bg-black bg-opacity-10 px-1 rounded">{finding.marking}</code>
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            finding.risk === 'High' ? 'bg-red-100 text-red-800' :
                            finding.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {finding.risk} Risk
                          </span>
                          <span className="text-sm font-medium">
                            {finding.confidence}% confidence
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-medium mb-2">Detected Matches:</h5>
                        <div className="flex flex-wrap gap-1">
                          {finding.matches.map((match, matchIndex) => (
                            <span
                              key={matchIndex}
                              className="px-2 py-1 bg-black bg-opacity-10 rounded text-sm font-mono"
                            >
                              "{match}"
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isAnalyzing && results.length === 0 && (files.length > 0 || textInput.trim()) && (
        <div className="text-center py-12">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No CUI Detected</h3>
          <p className="text-gray-600">
            The analyzed content does not appear to contain any Controlled Unclassified Information.
          </p>
        </div>
      )}

      <div className="mt-12 p-6 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Important Information
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p><strong>This tool provides automated CUI detection suggestions.</strong></p>
          <p>• Results require human review for accuracy</p>
          <p>• Based on official NARA CUI Registry categories</p>
          <p>• Consult information security professionals for high-risk findings</p>
          <p>• No data is stored or transmitted</p>
        </div>
      </div>
    </div>
  );
};

export default CUIDetectionTool;
// Build trigger
